import type { ComponentProps } from 'react';
import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useFocusEffect, useLocalSearchParams, useNavigation } from 'expo-router';

import { useResponsive, useIsFocused } from '@sg/garnish';

import { LocationSearch, TransparentModal } from '../../../../components';
import { useNavigateBack } from '../../../../navigation';
import { useLocationConfirm } from '../../hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const OrderProductSearchLocationScreen = () => {
  const { productSlug: baseProductSlug } = useLocalSearchParams<{
    productSlug?: string;
  }>();

  const isFocused = useIsFocused();
  const navigation = useNavigation();
  const navigateBack = useNavigateBack();

  const setOptions = navigation.setOptions;

  const handleDismiss = useCallback(() => {
    navigateBack();
  }, [navigateBack]);

  const { currentBreakpoint } = useResponsive();

  // ─────────────────────────────────────────────────────────────────────

  const [selectedLocationData, setSelectedLocationData] = useState<SelectedLocation>();

  const { isFetchingProduct, isProductNotAvailable, handleLocationConfirm } = useLocationConfirm({
    baseProductSlug: baseProductSlug ?? '',
    restaurantId: selectedLocationData?.restaurantId,
    restaurantSlug: selectedLocationData?.restaurantSlug,
    deliveryAddressId: selectedLocationData?.deliveryOrderDetailAddressId,
    deliveryVendorName: selectedLocationData?.deliveryOrderDetailVendorName,
    deliveryVendorRestaurantId: selectedLocationData?.deliveryOrderDetailVendorRestaurantId,
    deliveryFee: selectedLocationData?.deliveryOrderDetailDeliveryFee,
  });

  const onOpenRestaurant = useCallback<OnOpenRestaurant>((context) => {
    const { activeLocation } = context;

    if (!activeLocation) return;

    const { restaurantId, restaurantSlug } = activeLocation;

    setSelectedLocationData({ restaurantId, restaurantSlug });
  }, []);

  const onOpenDeliveryRestaurant = useCallback<OnOpenDeliveryRestaurant>((context) => {
    const { deliveryLocation } = context;

    if (!deliveryLocation) return;

    const {
      restaurantId,
      restaurantSlug,
      deliveryVendorRestaurantId,
      deliveryVendorName,
      restaurantDeliveryFee,
      addressId,
    } = deliveryLocation;

    setSelectedLocationData({
      restaurantId,
      restaurantSlug,
      deliveryOrderDetailAddressId: addressId,
      deliveryOrderDetailVendorName: deliveryVendorName,
      deliveryOrderDetailVendorRestaurantId: deliveryVendorRestaurantId,
      deliveryOrderDetailDeliveryFee: restaurantDeliveryFee,
    });
  }, []);

  // ─── Effects ─────────────────────────────────────────────────────────

  useLayoutEffect(() => {
    setOptions({
      headerShown: false,
    });
  }, [setOptions]);

  // navigate to the selected customization screen if it's available
  useEffect(() => {
    const isProductAvailable = selectedLocationData && !isFetchingProduct;

    if (isProductAvailable) {
      void handleLocationConfirm();
    }
  }, [isFetchingProduct, isProductNotAvailable, handleLocationConfirm, selectedLocationData]);

  // Reset state when navigating away
  useFocusEffect(
    useCallback(() => {
      setSelectedLocationData(undefined);
    }, []),
  );

  // ─────────────────────────────────────────────────────────────────────

  if (!isFocused) return null;

  return (
    <TransparentModal
      testID="change-location"
      disableAnimation={currentBreakpoint.isXS}
      size="large"
      offset="10%"
      onClose={handleDismiss}
    >
      <LocationSearch
        navigateToLocation={onOpenRestaurant}
        navigateToDeliveryLocation={onOpenDeliveryRestaurant}
        interactedLocationRestaurantSlug={selectedLocationData?.restaurantSlug}
        interactedLocationDeliveryAddressId={selectedLocationData?.deliveryOrderDetailAddressId}
        isInsideModal={true}
      />
    </TransparentModal>
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type OnOpenRestaurant = NonNullable<LocationSearchProps['navigateToLocation']>;

type OnOpenDeliveryRestaurant = NonNullable<LocationSearchProps['navigateToLocation']>;

type SelectedLocation = Readonly<{
  deliveryOrderDetailAddressId?: string;
  restaurantId?: string;
  restaurantSlug?: string;
  deliveryOrderDetailVendorName?: string;
  deliveryOrderDetailVendorRestaurantId?: string;
  deliveryOrderDetailDeliveryFee?: number;
}>;

type LocationSearchProps = ComponentProps<typeof LocationSearch>;
